/**
 * HTML Utility Functions
 * Centralized utilities for HTML string manipulation
 */

/**
 * Strip HTML tags from a string and decode HTML entities
 * Uses DOMParser for accurate entity handling (e.g., &amp; &nbsp; etc.)
 * 
 * @param {string} html - HTML string to clean
 * @returns {string} Plain text with HTML tags removed and entities decoded
 * 
 * @example
 * stripHtml('<p>Hello &amp; <strong>World</strong></p>')
 * // Returns: 'Hello & World'
 */
export function stripHtml(html) {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

/**
 * Strip HTML tags using regex (faster, but doesn't decode all entities)
 * Use this for performance-critical paths where entities are unlikely
 * 
 * @param {string} html - HTML string to clean
 * @returns {string} Plain text with HTML tags removed
 */
export function stripHtmlFast(html) {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, '')  // Remove HTML tags
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
}

/**
 * Truncate text to a maximum length with ellipsis
 * 
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Truncated text with ellipsis if needed
 */
export function truncateText(text, maxLength = 100) {
  if (!text || text.length <= maxLength) return text || '';
  return text.slice(0, maxLength).trim() + '...';
}

/**
 * Strip HTML and truncate in one operation
 * 
 * @param {string} html - HTML string to clean and truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Clean, truncated text
 */
export function stripHtmlAndTruncate(html, maxLength = 100) {
  return truncateText(stripHtml(html), maxLength);
}
